/**
 * Menu Configuration API Service
 *
 * Handles CRUD operations for menu configurations.
 * Uses mock mode (localStorage) when backend API is not available.
 */
import axios from 'axios';
import { getDefaultMenuConfig } from '../../defaultMenus';

function getAuthHeaders() {
  const user = JSON.parse(localStorage.getItem("user"));
  return {
    headers: {
      "Content-Type": "application/json",
      "Access-Control-Allow-Origin": true,
      crossorigin: true,
      "access-token": user ? user.token : "",
    },
  };
}

function getCurrentUserId() {
  const id = localStorage.getItem("userid");
  return id ? parseInt(id, 10) : null;
}

export async function getMenuConfig({ tenant_id, client_id, role_id, user_id }) {
  try {
    const response = await axios.get(process.env.REACT_APP_API_SERVER_HOST + '/v1/menu-config', {
      ...getAuthHeaders(),
      params: { tenant_id, client_id, role_id, user_id },
    });
    return response.data && response.data.data ? response.data.data : response.data;
  } catch (error) {
    console.warn("Failed to fetch menu config, using defaults:", error.message);
    return null;
  }
}

export async function resolveMenuConfig({ tenant_id, client_id, role_id, user_id }) {
  try {
    const response = await axios.get(process.env.REACT_APP_API_SERVER_HOST + '/v1/menu-config/resolve', {
      ...getAuthHeaders(),
      params: { tenant_id, client_id, role_id, user_id },
    });
    return response.data && response.data.data ? response.data.data : response.data;
  } catch (error) {
    console.warn("Failed to resolve menu config, using defaults:", error.message);
    return null;
  }
}

export async function saveMenuConfig(payload) {
  const currentUserId = getCurrentUserId();
  const enrichedPayload = {
    ...payload,
    ...(currentUserId !== null && { created_by: currentUserId, updated_by: currentUserId }),
  };
  try {
    const response = await axios.post(
      process.env.REACT_APP_API_SERVER_HOST + '/v1/menu-config',
      JSON.stringify(enrichedPayload),
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    console.error("Failed to save menu config:", error.message);
    throw error;
  }
}

export async function updateMenuConfig(id, payload) {
  const currentUserId = getCurrentUserId();
  const enrichedPayload = {
    ...payload,
    ...(currentUserId !== null && { updated_by: currentUserId }),
  };
  try {
    const response = await axios.put(
      process.env.REACT_APP_API_SERVER_HOST + '/v1/menu-config/' + id,
      JSON.stringify(enrichedPayload),
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    console.error("Failed to update menu config:", error.message);
    throw error;
  }
}

export async function deleteMenuConfig(id) {

  try {
    const response = await axios.delete(
      process.env.REACT_APP_API_SERVER_HOST + '/v1/menu-config/' + id,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    console.error("Failed to delete menu config:", error.message);
    throw error;
  }
}

export async function getMenuConfigsByTenant(tenant_id, { limit = 50, offset = 0 } = {}) {

  try {
    const response = await axios.get(process.env.REACT_APP_API_SERVER_HOST + '/v1/menu-config/list', {
      ...getAuthHeaders(),
      params: { tenant_id, limit, offset },
    });
    return response.data && response.data.data ? response.data.data : [];
  } catch (error) {
    console.warn("Failed to fetch tenant menu configs:", error.message);
    return [];
  }
}

// Roles that never have a custom menu — skip the resolve call entirely at login.
const ROLES_WITHOUT_MENU = [30];

export async function fetchMenuConfigForLogin(user) {
  try {
    // Roles in ROLES_WITHOUT_MENU have no custom menu — use defaults immediately.
    if (ROLES_WITHOUT_MENU.includes(Number(user.role))) {
      console.log('[MenuConfig] Role', user.role, 'has no custom menu, using defaults.');
      return null;
    }

    const userId = user.custom_user_id || user.user_id;
    console.log('[MenuConfig] Fetching for:', { tenant_id: user.tenant_id, client_id: user.client_id, role_id: user.role, user_id: userId });

    // Single API call — the backend handles the fallback for Clients (role_id=1) internally:
    // if user_id is given but no user-scoped record exists AND role_id=1,
    // the API returns the role-scoped config for the same client.
    const config = await resolveMenuConfig({
      tenant_id: user.tenant_id,
      client_id: user.client_id || null,
      role_id: user.role,
      user_id: userId,
    });
    console.log('[MenuConfig] API returned:', config);

    if (config && config.menu_config) {
      const menuItems = config.menu_config;
      // Handle case where menu_config might be a string (double-encoded JSON)
      if (typeof menuItems === 'string') {
        try {
          return JSON.parse(menuItems);
        } catch (e) {
          console.error('[MenuConfig] Failed to parse menu_config string:', e);
          return null;
        }
      }
      return menuItems;
    }
    console.log('[MenuConfig] No menu_config found, using defaults');
    return null;
  } catch (e) {
    console.error('[MenuConfig] fetchMenuConfigForLogin error:', e);
    return null;
  }
}


